import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { range } from "lodash";
import { PulseLoader } from "react-spinners";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons/faStar";
import { faStar as faOpenStar } from "@fortawesome/free-regular-svg-icons/faStar";

const FeedbackStar = ({
  isFilled = false,
  isChecked = false,
  isTabStop = false,
  label,
  buttonRef,
  onClick = () => { },
}) => {
  const icon = isFilled ? faStar : faOpenStar;

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isChecked}
      aria-label={label}
      tabIndex={isTabStop ? 0 : -1}
      ref={buttonRef}
      className={
        `star-button ${isFilled ? "filled" : "open"}`
      }
      onClick={() => onClick()}>
      <FontAwesomeIcon icon={icon} />
    </button>
  );
};

FeedbackStar.propTypes = {
  isFilled: PropTypes.bool,
  isChecked: PropTypes.bool,
  isTabStop: PropTypes.bool,
  label: PropTypes.string,
  buttonRef: PropTypes.func,
  onClick: PropTypes.func,
};

// A radio group: one star is a Tab stop, arrow keys move the rating, and each star
// is announced as "n / max" with its checked state.
const FeedbackRatingInput = ({
  score = 0,
  maxScore = 5,
  isDirty = false,
  labelledBy,
  onChange = () => { },
}) => {
  const { t } = useTranslation("components");
  const starRefs = useRef([]);

  const onKeyDown = (e) => {
    const keys = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1 };
    if (!(e.key in keys)) return;

    e.preventDefault();
    const next = Math.min(maxScore, Math.max(1, (score || 0) + keys[e.key]));
    onChange(next);
    if (starRefs.current[next - 1]) starRefs.current[next - 1].focus();
  };

  return (
    <div className="feedback-rating-input">
      <div className="feedback-stars" role="radiogroup" aria-labelledby={labelledBy} onKeyDown={onKeyDown}>
        {range(maxScore).map((i) => (
          <FeedbackStar
            key={i}
            isFilled={i < score}
            isChecked={i + 1 === score}
            isTabStop={score > 0 ? i + 1 === score : i === 0}
            label={`${i + 1} / ${maxScore}`}
            buttonRef={(element) => { starRefs.current[i] = element; }}
            onClick={() => onChange(i + 1)} />
        ))}
      </div>
      {isDirty && (
        <div className="dirty-indicator" role="status">
          <PulseLoader size={6} color="white" />
          <span className="visually-hidden">{t("status.saving")}</span>
        </div>
      )}
    </div>
  );
};

FeedbackRatingInput.propTypes = {
  score: PropTypes.number,
  maxScore: PropTypes.number,
  isDirty: PropTypes.bool,
  // Id of the element that names the rating.
  labelledBy: PropTypes.string,
  onChange: PropTypes.func,
};

export default FeedbackRatingInput;
